import React, { useContext, useState, Suspense } from 'react';
import { isEmpty, merge } from 'lodash';
import { IClickData, getBuyerState, getMixPanelCommonProperties, sendClickEvent } from '@fiverr-private/homepage_utils';
import { IReportClickArgs } from '@fiverr-private/primary_section_sdk';
import { mixpanel } from '@fiverr-private/obs';
import WithImpression from '../../../../utils/reporting/bigQuery/impressions/components/withImpression';
import { ThemeContext } from '../../../context/theme/theme.context';
import { useUser } from '../../../context/user/userProvider';
import { usePrimaryEnrichedData } from '../utils';
import { PrimaryComponentSkeleton } from './PrimaryComponentSkeleton';
import { PrimaryComponentProps } from './PrimaryComponent.types';

const PrimaryComponent = ({ PrimaryComponent, primaryEnrichedData, componentName }: PrimaryComponentProps) => {
    const { layoutId } = useContext(ThemeContext);
    const { buyerInsights } = useUser();
    const buyerState = getBuyerState(buyerInsights?.segment);
    const mixPanelEventPrefix = `${componentName} Section`;
    const [impressionId, setImpressionId] = useState<string>('');
    const primarySize = primaryEnrichedData?.size;

    const onImpressionIdAssign = (impressionId: string) => {
        setImpressionId(impressionId);
    };

    const reportPrimaryComponentClick = (
        args: IReportClickArgs,
        impressionId: string,
        primaryEnrichedData: ReturnType<typeof usePrimaryEnrichedData>
    ) => {
        const commonClickData: IClickData = {
            imp: {
                id: impressionId,
            },
        };
        const { sub_category, nested_sub_category } = args;
        const projectId = primaryEnrichedData?.biEnrichment.projectId;
        const enrichedClickData = {
            ...merge(commonClickData, args),
            size: primarySize,
            page_element_name: `${primaryEnrichedData?.biEnrichment?.componentType}_${componentName}`,
        };

        sendClickEvent(enrichedClickData);
        mixpanel.track(`Home - ${mixPanelEventPrefix} Click`, {
            ...(nested_sub_category?.id || sub_category?.id
                ? { 'SubCategory Id': nested_sub_category?.id || sub_category?.id }
                : {}),
            ...(projectId ? { 'Project Id': projectId } : {}),
            ...(primarySize ? { 'Component Size': primarySize } : {}),
            ...getMixPanelCommonProperties({
                layoutId,
                buyerState,
                componentName: `${primaryEnrichedData?.name}_${componentName}`,
            }),
        });
    };

    return isEmpty(primaryEnrichedData) ? (
        <PrimaryComponentSkeleton />
    ) : (
        <Suspense fallback={<PrimaryComponentSkeleton />}>
            <WithImpression
                onImpressionIdAssign={onImpressionIdAssign}
                biEnrichment={{
                    layoutId,
                    buyerState,
                    componentName,
                    primarySize,
                    componentType: primaryEnrichedData.biEnrichment?.componentType,
                    order: primaryEnrichedData.biEnrichment?.order,
                    projectId: primaryEnrichedData.biEnrichment?.projectId,
                }}
                mixPanelEventPrefix={mixPanelEventPrefix}
            >
                <PrimaryComponent
                    {...primaryEnrichedData}
                    reportClick={(args: IReportClickArgs) =>
                        reportPrimaryComponentClick(args, impressionId, primaryEnrichedData)
                    }
                />
            </WithImpression>
        </Suspense>
    );
};

export default PrimaryComponent;
